<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { fetchData } from '@/utils/fetch'
import { buriedPointUtil, openURLWithChannelQuery, throttle } from '@/utils/utils'
import FeaturedCategory from './FeaturedCategory.vue'
import Icon from '../Icon/Icon.vue'
import { useValidCache } from '@/hooks/useValidCache'
import storage from '@/utils/storage'
import { useLanguageStore } from '@/pinia/language'
import { PageContainer } from '../PageLayout'

const router = useRouter()
const route = useRoute()
const store = useStore()

// 数据参数
const loading = ref(false) // loading 状态
const categories = ref([]) // 类目树数据
const activeCategory = reactive({}) // 当前激活的一级类目
const activeSubcategory = ref(null) // 当前激活的二级类目
const activeThirdCategory = ref(null) // 当前激活的三级类目
const isAllBtnActive = ref(false) // 是否激活全部类目按钮

const subCategoryConRef = ref(null) // 二级类目容器
const itemRefs = ref({}) // 三级类目 ref

const sliderCategories = ref([]) // 滚动类目列表
const scrollContainer = ref(null) // 滚动容器
const isLeftDisabled = ref(true) // 左滚动按钮是否禁用
const isRightDisabled = ref(false) // 右滚动按钮是否禁用

const allCategory = ref([])
const categorySubIndex = ref(0)

const language = useLanguageStore()
const categoryVisible = computed(() => store.state.common.categoryVisible)

const openGuidancePop = () => {

}

const scrollLeft = throttle(() => {
  const left = scrollContainer.value.scrollLeft - 600
  scrollContainer.value.scrollTo({
    left,
    behavior: 'smooth'
  })
  isLeftDisabled.value = left <= 0
  isRightDisabled.value = false
}, 300)

const scrollRight = throttle(() => {
  const left = scrollContainer.value.scrollLeft + 600
  scrollContainer.value.scrollTo({
    left,
    behavior: 'smooth'
  })
  isLeftDisabled.value = false
  isRightDisabled.value = left >= scrollContainer.value.scrollWidth - scrollContainer.value.clientWidth
}, 300)

// 请求一级类目
const fetchCategories = async () => {
  const format = (data) => {
    fetchAllCategory()
    categories.value = data
    let clickedCategories = JSON.parse(localStorage.getItem('clickedCategories')) || []
    clickedCategories = clickedCategories.filter(item => item)

    const length = clickedCategories.length
    let newData = []
    // 固定前三个一级类目
    for (let i = 0; i < length; i++) {
      const item = data.find(item => item.categoryId === clickedCategories[i])
      newData.push(item)
      data = data.filter(item => item.categoryId !== clickedCategories[i])
    }
    // 完整类目
    newData = [...newData, ...data]
    sliderCategories.value = newData
    if (window.location.pathname.includes('/offer')) {
      setActiveCategory('', true)
    } else {
      setActiveCategory('', true)
    }
  }

  const { cacheData, CACHE_VERSION } = useValidCache('cacheShopCategory')
  if (cacheData.value) {
    format(cacheData.value.data)
    return
  }

  loading.value = true
  const { code, data } = await fetchData(
    `/dsp-app/alibaba-common/firstLevelCategory?language=${language.current}`,
    {
      method: 'get'
    }
  )
  if (code === 200) {
    storage.set('cacheShopCategory', {
      data,
      timestamp: Date.now(),
      cache_version: CACHE_VERSION
    })
    format(data)
  } else {
    console.error('获取类目失败')
  }
  loading.value = false
}

const fetchAllCategory = () => {
  const { cacheData, CACHE_VERSION } = useValidCache('cacheAllCategory')
  if (cacheData.value) {
    allCategory.value = cacheData.value.data
    return
  }

  fetchData(
    `/dsp-app/alibaba-common/allCategory?language=${language.current}`,
    {
      method: 'get'
    }
  ).then((res) => {
    if (res.code === 200) {
      storage.set('cacheAllCategory', {
        data: res.data,
        timestamp: Date.now(),
        cache_version: CACHE_VERSION
      })
      allCategory.value = res.data
    }
  })
}

// 选择全部类目
const selectAllCategories = () => {
  isAllBtnActive.value = true
  const ppId = route.query.ppId
  const pId = route.query.pId
  const id = route.query.categoryId
  buriedPointUtil({ eventId: 'PWH401' })
  if (id && ppId) {
    selectCategory(ppId, pId, id)
  } else {
    setActiveCategory('', true)
  }
  store.commit('common/SET_CATEGORY_VISIBLE', true)
}

// 设置 ref
const setRef = (el, child) => {
  if (el) {
    itemRefs.value[child.categoryId] = el
  }
}

// 设置一级类目并请求完整子类目树
const setActiveCategory = async (category, needBuried) => {
  if (!needBuried) {
    buriedPointUtil({
      eventId: 'PWH402',
      categoryId: category?.categoryId,
      categoryName: category?.categoryName
    })
  }
  const newActiveCategory = reactive({ ...category })
  activeCategory.value = newActiveCategory

  /**
   * 若 category 为 '', 则不触发滚动及修改
   */
  if (category === '' || category == null) return

  let clickedCategories = JSON.parse(localStorage.getItem('clickedCategories')) || []
  clickedCategories = clickedCategories.filter(item => item)
  if (clickedCategories.indexOf(newActiveCategory?.categoryId) === -1) {
    clickedCategories.unshift(newActiveCategory?.categoryId)
    // 只取最前面三个值
    if (clickedCategories.length > 3) {
      clickedCategories = clickedCategories.slice(0, 3)
    }
  } else {
    const index = clickedCategories.findIndex(item => item === activeCategory.value.categoryId)
    clickedCategories.splice(index, 1)
    clickedCategories.unshift(newActiveCategory?.categoryId)
  }
  localStorage.setItem('clickedCategories', JSON.stringify(clickedCategories))
}

const mouseenterFirstCategory = async (category, index) => {
  categorySubIndex.value = index
  const newActiveCategory = reactive({ ...category })
  activeCategory.value = newActiveCategory
}

const mouseenterSubCategory = async (sub, index) => {
  activeSubcategory.value = sub
  activeThirdCategory.value = {}
  // 滚动到对应的三级类目标题
  nextTick(() => {
    const activeElement = itemRefs.value[activeSubcategory.value?.categoryId]
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  })
}

// 设置二级类目
const setActiveSubcategory = (sub) => {
  activeSubcategory.value = sub
  activeThirdCategory.value = {}
  // 滚动到对应的三级类目标题
  nextTick(() => {
    const activeElement = itemRefs.value[activeSubcategory.value?.categoryId]
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  })
}

// 设置三级类目
const setActiveThirdCategory = (third, parentChild, ppObj) => {
  activeThirdCategory.value = third
  const { href } = router.resolve({
    path: '/search',
    query: {
      categoryId: third.categoryId,
      categoryName: third.categoryName,
      pName: parentChild.categoryName,
      pId: parentChild.categoryId,
      ppName: ppObj.categoryName,
      ppId: ppObj.categoryId
    }
  })
  store.commit('common/SET_CATEGORY_VISIBLE', false)
  openURLWithChannelQuery(href)
}

// 点击sliderItem
const clickSliderItem = (item) => {
  setActiveCategory(item, false)
  isAllBtnActive.value = false
  store.commit('common/SET_CATEGORY_VISIBLE', true)

  const _index = allCategory.value?.findIndex(v => v.categoryId === item.categoryId)
  if (_index !== -1) {
    categorySubIndex.value = _index
  }
}
// 点击最右侧的第二级类目
const clickSubCategory = (sub) => {
  setActiveSubcategory(sub)
  activeThirdCategory.value = sub
  const { href } = router.resolve({
    path: '/search',
    query: {
      categoryId: sub.categoryId,
      categoryName: sub.categoryName,
      ppName: activeCategory.value.categoryName,
      ppId: activeCategory.value.categoryId
    }
  })
  store.commit('common/SET_CATEGORY_VISIBLE', false)
  openURLWithChannelQuery(href)
}
// 选中类目
const selectCategory = (ppId, pId, id) => {
  if (ppId) {
    const activeCategory = allCategory.value.find(
      (item) => item.categoryId === ppId
    )
    if (activeCategory) {
      setActiveCategory(activeCategory)
      const _index = allCategory.value?.findIndex(v => v.categoryId === ppId)
      if (_index !== -1) {
        categorySubIndex.value = _index
      }
    }
  }
  if (pId && activeCategory?.childCategoryList) {
    console.log(Object.values(activeCategory?.children), 'activeCategory?.children')
    const activeSubcategory = Object.values(activeCategory?.children).find(
      (item) => item.categoryId === pId
    )
    if (activeSubcategory) {
      setActiveSubcategory(activeSubcategory)
    }
  }
  if (id && activeSubcategory?.childCategoryList) {
    const activeThirdCategory = activeSubcategory?.childCategoryList.find(
      (item) => item.categoryId === id
    )
    if (activeThirdCategory) {
      setActiveThirdCategory(activeThirdCategory)
    }
  }
}

watch(
  () => language.current,
  (newVal, oldValue) => {
    if (newVal) {
      if (oldValue && newVal !== oldValue) {
        storage.remove('cacheAllCategory')
        storage.remove('cacheShopCategory')
      }
      fetchCategories()
    }
  },
  { immediate: true }
)

const isActive = (item) => {
  const id = activeCategory.value.categoryId
  return item.categoryId === id && route.fullPath !== '/' && !isAllBtnActive
}

const hiddenCategory = () => {
  isAllBtnActive.value = false
  store.commit('common/SET_CATEGORY_VISIBLE', false)
}

onMounted(() => {
  window.addEventListener('scroll', e => {
    if (categoryVisible.value) {
      isAllBtnActive.value = false
      store.commit('common/SET_CATEGORY_VISIBLE', false)
    }
  })
})
</script>

<template>
  <div
    class="category-wrapper"
    @click="openGuidancePop"
  >
    <!-- 热搜词滚动条 -->
    <PageContainer class="category-slider">
      <div
        :class="{ active: isAllBtnActive }"
        class="category-slider-nav"
        @click="selectAllCategories"
      >
        <Icon
          class="category-slider-nav__icon"
          name="category-all-l"
        />
        All Categories
        <Icon
          class="category-slider-nav__arrow"
          name="arrow-down-l-2"
        />
      </div>
      <div class="category-slider__divider" />
      <!-- 类目列表 -->
      <div
        ref="scrollContainer"
        class="category-slider-list"
      >
        <FeaturedCategory @click="store.commit('common/SET_CATEGORY_VISIBLE', false)" />
        <div
          :class="[
            {
              active: isActive(item),
            },
            'category-slider-list__item',
          ]"
          v-for="(item, index) in sliderCategories"
          :key="item.categoryName + '-' + index"
          :ref="(el) => setRef(el, item)"
          @click="clickSliderItem(item)"
        >
          {{ item.categoryName }}
        </div>
      </div>
      <!-- 左右按钮 -->
      <div class="arrow-wrapper">
        <div
          @click="scrollLeft"
          :class="{ disabled: isLeftDisabled }"
          class="icon-wrapper"
        >
          <Icon
            class="icon"
            name="icon-arrow-left-l"
          />
        </div>
        <div
          @click="scrollRight"
          :class="{ disabled: isRightDisabled }"
          class="icon-wrapper"
        >
          <Icon
            class="icon"
            name="arrow-right-l-2"
          />
        </div>
      </div>
    </PageContainer>

    <!-- 类目导航 -->
    <div
      v-if="categoryVisible"
      class="category-slider-wrapper"
      @click="hiddenCategory"
    >
      <PageContainer
        class="category-nav"
        @click.stop
      >
        <!-- 一级类目 -->
        <div class="categories">
          <h2 class="categories-header-title">
            Shop by Categories
          </h2>
          <ul>
            <li
              v-for="(category, index) in allCategory"
              :key="category.categoryName"
              @mouseenter="mouseenterFirstCategory(category, index)"
              :class="{
                active:
                  activeCategory?.value?.categoryId === category?.categoryId,
              }"
            >
              <span class="first-categoryName">{{ category.categoryName }}</span>
              <Icon
                style="color: #B2B7C2;"
                name="arrow-right-l-2"
              />
            </li>
          </ul>
        </div>
        <!-- 二级类目 -->
        <div
          class="subcategories"
          v-if="allCategory[categorySubIndex]?.childCategoryList"
          ref="subCategoryConRef"
        >
          <h3 class="sub-categoryName">
            {{ allCategory[categorySubIndex]?.categoryName }}
          </h3>
          <div class="subcategory-list">
            <div
              v-for="(sub, index) in allCategory[categorySubIndex].childCategoryList"
              :key="sub.categoryId"
              @mouseenter="mouseenterSubCategory(sub, index)"
            >
              <span class="sub-categoryName-label">{{ sub.categoryName }}</span>
              <Icon
                style="color: #B2B7C2;"
                name="arrow-right-l-2"
              />
            </div>
          </div>
        </div>
        <!-- 三级类目 -->
        <div
          class="third-level"
          v-if="allCategory[categorySubIndex]?.childCategoryList"
        >
          <ul>
            <li
              v-for="(child, index) in allCategory[categorySubIndex]?.childCategoryList"
              :key="child.id + '-' + index"
              :ref="(el) => setRef(el, child)"
            >
              <h3
                :class="{
                  active: child.categoryId === activeThirdCategory?.categoryId, 'sub-categoryName-label' : true
                }"
                @click="clickSubCategory(child)"
              >
                {{ child.categoryName }}
              </h3>
              <ul>
                <li
                  v-for="(grandson, index) in child?.childCategoryList"
                  :key="grandson.categoryId + '-' + index"
                  :class="[
                    {
                      active:
                        grandson?.categoryId === activeThirdCategory?.categoryId,
                    },
                    'grandson-item',
                  ]"
                  @click="setActiveThirdCategory(grandson, child, allCategory[categorySubIndex])"
                >
                  {{ grandson.categoryName }}
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </PageContainer>
    </div>
  </div>
</template>

<style lang="less" scoped>
@import "./index.less";
</style>
